/**
 * @file Gives each measure's bar percent as an exact share of the largest
 * measure, rounded to one place and clamped to 0 through 100. Consumers:
 * `analyticsPipeline` and `analyticsReceivableAging`. The largest measure is
 * found by cross-multiplication, so no fraction is reduced or turned into a
 * float before the share. When the largest numerator is not above zero, every
 * bar is 0, so an all-zero list draws no bar. An empty list gives `[]`.
 */
import { analyticsFractionDecimal } from '#types';
import type { AnalyticsFraction } from '#types';

const barPercent = (
  measure: AnalyticsFraction,
  largest: AnalyticsFraction,
): number => {
  if (largest.numerator <= 0n) return 0;
  const percent = Number(
    analyticsFractionDecimal(
      {
        numerator: measure.numerator * largest.denominator * 100n,
        denominator: measure.denominator * largest.numerator,
      },
      1,
    ),
  );
  return Math.min(100, Math.max(0, percent));
};

export const analyticsBarPercents = (
  measures: readonly AnalyticsFraction[],
): readonly number[] => {
  if (measures.length === 0) return [];
  const largest = measures.reduce((max, measure) =>
    measure.numerator * max.denominator > max.numerator * measure.denominator
      ? measure
      : max,
  );
  return measures.map((measure) => barPercent(measure, largest));
};
